import { getEnvVariable } from '@/env'
import { withBase } from './withBase'

const LOGIN_PATH = '/login'
const CUSTOMER_BASE = '/customer'

// Deployed Phoenix CA runs at /customer/* on real hosts.
// Local dev and CI e2e stay on localhost with in-app login.
const isDeployed = (): boolean => {
  const { hostname } = window.location
  if (hostname === 'localhost' || hostname === '127.0.0.1') return false

  const base = (import.meta.env.VITE_BASE_URL ?? '/').replace(/\/+$/, '')
  return base === CUSTOMER_BASE
}

const unifiedOrigin = (): string => getEnvVariable('lyka_url')

// Browser pathname includes the app base (/customer/account).
// Router paths are relative to the base (/account).
// Used for cold-load 401 handling before the router has initialized.
export const browserPathToRouter = (): string => {
  const base = (import.meta.env.VITE_BASE_URL ?? '/').replace(/\/+$/, '')
  const { pathname } = window.location

  if (!base || base === '/') return pathname
  if (pathname === base) return '/'

  return pathname.startsWith(`${base}/`) ? pathname.slice(base.length) || '/' : pathname
}

// Full router-relative path (path + query + hash) for the browser's current URL
export const browserFullPathToRouter = (): string =>
  browserPathToRouter() + window.location.search + window.location.hash

export const buildLoginQuery = (
  routerFullPath: string,
  existingQuery: Record<string, unknown> = {},
): Record<string, string | string[]> => {
  const routerPath = routerFullPath.split('?')[0]?.split('#')[0] ?? ''

  if (routerPath === LOGIN_PATH || routerPath === '/') {
    return existingQuery as Record<string, string | string[]>
  }

  if (
    !routerFullPath.startsWith('/') ||
    routerFullPath.startsWith('//') ||
    routerFullPath.includes('://') ||
    routerFullPath.includes('\\')
  ) {
    return existingQuery as Record<string, string | string[]>
  }

  // On deployed, unified login requires the redirect to start with /customer.
  // On local, Login.vue reads and pushes it directly as a router path — no prefix needed.
  const redirect = isDeployed() ? `${CUSTOMER_BASE}${routerFullPath}` : routerFullPath

  return { ...existingQuery, redirect }
}

const isSafeRouterPath = (path: unknown): boolean =>
  typeof path === 'string' &&
  path.startsWith('/') &&
  !path.startsWith('//') &&
  !path.includes('://') &&
  !path.includes('\\')

// Validates and returns the redirect param from the login page query string.
// Falls back to null if missing or unsafe — caller should default to deliveries.
export const resolvePostLoginRedirect = (query: Record<string, unknown>): string | null => {
  const redirect = query.redirect
  if (!isSafeRouterPath(redirect)) return null

  let path = redirect as string

  // Unified login stores browser paths (/customer/...); the router expects paths relative to base.
  if (path === CUSTOMER_BASE || path.startsWith(`${CUSTOMER_BASE}/`)) {
    path = path.slice(CUSTOMER_BASE.length) || '/'
  }

  return path
}

const queryString = (query: Record<string, string | string[]>): string => {
  const params = new URLSearchParams(query as Record<string, string>).toString()
  return params ? `?${params}` : ''
}

export const goToLogin = (search = ''): void => {
  if (isDeployed()) {
    window.location.replace(`${unifiedOrigin()}${LOGIN_PATH}${search}`)
  } else {
    window.location.href = `${withBase('login')}${search}`
  }
}

// Router guard calls this when user navigates to /login on a deployed host.
// Hand off to unified login before rendering the Phoenix login form.
export const handOffLoginRoute = (fullPath: string, path: string): void => {
  window.location.replace(`${unifiedOrigin()}${LOGIN_PATH}${fullPath.slice(path.length)}`)
}

// Session expired (401 from API). Send user to login and return them to current page.
export const redirectToLogin = (): void => {
  const current = browserFullPathToRouter()
  const path = current.split('?')[0]?.split('#')[0] ?? ''

  if (path === LOGIN_PATH) return

  goToLogin(queryString(buildLoginQuery(current)))
}

export const shouldHandOffToUnifiedLogin = isDeployed
